import type { MessageInitShape } from '@bufbuild/protobuf';

import type {
  AdClickContextSchema,
  CampaignContextSchema,
  CustomerContextSchema,
  LeadContextSchema,
  PropertyPageContextSchema,
  SellDirectContextSchema,
} from './__generated__/protobuf/journey/data/contexts_pb';
import type { WebsiteSource } from './__generated__/protobuf/journey/data/sources_pb';
import type { SegmentCompatibleData } from './internal/analytics';
import { getDeployEnv } from './internal/deployEnv';

type AdClickContext = MessageInitShape<typeof AdClickContextSchema>;
type CampaignContext = MessageInitShape<typeof CampaignContextSchema>;
type CustomerContext = MessageInitShape<typeof CustomerContextSchema>;
type LeadContext = MessageInitShape<typeof LeadContextSchema>;
type PropertyPageContext = MessageInitShape<typeof PropertyPageContextSchema>;
type SellDirectContext = MessageInitShape<typeof SellDirectContextSchema>;

/** Contextual data that is useful to send to observability services. */
export interface ContextualData {
  /** Data about the current user. */
  user?: {
    humanID: string;
    username?: string;
    email?: string;
  };
  /** structured ad click context from the strong schema */
  adClickContext?: AdClickContext;
  /** structured campaign context from the strong schema */
  campaignContext?: CampaignContext;
  /** structured customer context from the strong schema */
  customerContext?: CustomerContext;
  /** structured lead context from the strong schema */
  leadContext?: LeadContext;
  /** structured sell direct context from the strong schema */
  sellDirectContext?: SellDirectContext;
  /** Version and listing identity of the web PDP hosting this event. */
  propertyPageContext?: PropertyPageContext;
  /** Unstructured key-value data used in trackEvent and trackError */
  data: SegmentCompatibleData<{ [k: string]: unknown }>;
}

export const emptyContextualData: Readonly<ContextualData> = {
  user: undefined,
  data: {},
  adClickContext: undefined,
  campaignContext: undefined,
  customerContext: undefined,
  leadContext: undefined,
  sellDirectContext: undefined,
  propertyPageContext: undefined,
};

export type JourneyEventContextualData = Pick<
  ContextualData,
  'customerContext' | 'campaignContext' | 'propertyPageContext'
> & {
  /** Optional overrides for the WebsiteSource we attach to initial page view journey events */
  websiteSourceContext?: Partial<WebsiteSource> & { market?: string };
};

export const emptyJourneyEventContextualData: Readonly<JourneyEventContextualData> = {
  customerContext: undefined,
  campaignContext: undefined,
  websiteSourceContext: undefined,
  propertyPageContext: undefined,
};

/**
 * Merges 2 ContextualDatas. The right argument (child)'s data take precedence.
 * data: parent's and child's data will be merged.
 * all structured contexts, including user: should not be set >1 times. If set >1 times, child's campaignContext will override parent's.
 */
export function mergeContextualData(
  parent: ContextualData,
  child: Partial<ContextualData> = emptyContextualData,
): ContextualData {
  if (getDeployEnv() !== 'production' && parent.user && child.user) {
    throw new Error('User already set. This error only shows in non-production environments.');
  }

  return {
    user: child.user || parent.user,
    data: { ...parent.data, ...child.data },
    campaignContext: child.campaignContext || parent.campaignContext,
    customerContext: child.customerContext || parent.customerContext,
    leadContext: child.leadContext || parent.leadContext,
    sellDirectContext: child.sellDirectContext || parent.sellDirectContext,
    adClickContext: child.adClickContext || parent.adClickContext,
    propertyPageContext: child.propertyPageContext || parent.propertyPageContext,
  };
}
